// lint-internal-leaks-ignore-next-line: framework-script — load-bearing visual regression marker
// @visual-diff-render: vite
/**
 * @file Toggle — a styled wrapper around MUI's Switch
 * @version 0.1.0
 */

import React, { forwardRef } from 'react';
import MuiSwitch, { type SwitchProps } from '@mui/material/Switch';

/**
 * Heidrick Toggle — a styled wrapper around MUI's Switch, re-exported under
 * the Heidrick-preferred name.
 *
 * Visual styling is applied via `heidrickTheme.components.MuiSwitch` — wrap
 * your app in `<ThemeProvider theme={heidrickTheme}>` for the Heidrick
 * track / thumb / glyph treatment to apply.
 *
 * The checked ("on") state uses the theme's default accent (Heidrick navy).
 * Pass `accentColor` to opt into a per-instance on-state color — it recolors
 * the track fill, track border, checkmark glyph, and the checked focus ring.
 * Off / disabled states are untouched.
 *
 * @example
 * <Toggle defaultChecked />
 * <Toggle disabled />
 * <Toggle defaultChecked accentColor="var(--hd-raw-color-teal-accessible)" />
 */
export const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(
  function Toggle({ accentColor, sx, ...props }, ref) {
    const accentSx = accentColor
      ? {
          // Checkmark glyph (default + hover) when on.
          '& .MuiSwitch-switchBase.Mui-checked': { color: accentColor },
          '& .MuiSwitch-switchBase.Mui-checked:not(.Mui-disabled):hover, & .MuiSwitch-switchBase.Mui-checked[data-force-hover]:not(.Mui-disabled)':
            { color: accentColor },
          // Track fill + border when on.
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: accentColor,
            borderColor: accentColor,
          },
          // Outer focus ring matches the accent when on + focused.
          '&:has(.MuiSwitch-switchBase.Mui-checked.Mui-focusVisible), &:has(.MuiSwitch-switchBase.Mui-checked[data-force-focus])':
            { outlineColor: accentColor },
        }
      : null;
    return (
      <MuiSwitch
        ref={ref}
        sx={[accentSx, ...(Array.isArray(sx) ? sx : [sx])] as ToggleProps['sx']}
        {...props}
      />
    );
  },
);

export interface ToggleProps extends SwitchProps {
  /**
   * Opt-in override for the checked ("on") accent color. Recolors the track
   * fill, track border, checkmark glyph, and checked focus ring. Accepts any
   * CSS color (a `var(--hd-*)` token is preferred). Omit to use the theme
   * default. Off / disabled states are unaffected.
   */
  accentColor?: string;
}
